import { useState, useEffect, useCallback } from 'react';
import { getSREDReport, getSREDProjects, getSREDConsultants, SREDReportResponse, SREDConsultant } from 'api/sred';
import { useNotificationProvider } from 'hooks';
import { SREDParams } from '../types';

function useSREDReport() {
  const { notifyError } = useNotificationProvider();

  const [params, setParams] = useState<SREDParams>(() => ({
    startDate: '2022-11-01',
    endDate: '2023-10-31',
    sredYear: '2023',
    selectedConsultantIds: [],
    selectedProjects: [],
  }));

  const [reportData, setReportData] = useState<SREDReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Available options fetched from backend
  const [availableConsultants, setAvailableConsultants] = useState<SREDConsultant[]>([]);
  const [availableProjects, setAvailableProjects] = useState<string[]>([]);

  const canFetch = Boolean(params.startDate && params.endDate && params.sredYear);

  // Fetch available consultants and projects when date range / year changes
  useEffect(() => {
    if (!params.startDate || !params.endDate || !params.sredYear) return;

    const fetchOptions = async () => {
      try {
        const [consultants, projects] = await Promise.all([
          getSREDConsultants(params.startDate!, params.endDate!, params.sredYear),
          getSREDProjects(params.sredYear),
        ]);
        setAvailableConsultants(consultants);
        setAvailableProjects(projects);
      } catch (error) {
        console.error('[SREDReport] Failed to fetch filter options:', error);
      }
    };

    fetchOptions();
  }, [params.startDate, params.endDate, params.sredYear]);

  // Fetch report when params change (including selections)
  useEffect(() => {
    if (!canFetch) return;

    const fetchReport = async () => {
      setIsLoading(true);
      setHasFetched(true);
      try {
        const result = await getSREDReport({
          startDate: params.startDate,
          endDate: params.endDate,
          sredYear: params.sredYear,
          consultantIds: params.selectedConsultantIds.length > 0 ? params.selectedConsultantIds : undefined,
          projects: params.selectedProjects.length > 0 ? params.selectedProjects : undefined,
        });
        setReportData(result);
      } catch (error) {
        console.error('[SREDReport] Failed to fetch report:', error);
        notifyError('Failed to load SRED report data');
        setReportData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.startDate, params.endDate, params.sredYear, params.selectedConsultantIds, params.selectedProjects]);

  const onParamsChange = useCallback((updates: Partial<SREDParams>) => {
    setParams((prev) => ({ ...prev, ...updates }));
    setHasFetched(false);
  }, []);

  return {
    state: {
      params,
      reportData,
      isLoading,
      hasFetched,
      canFetch,
      availableConsultants,
      availableProjects,
    },
    handler: {
      onParamsChange,
    },
  };
}

export default useSREDReport;
